import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import * as d3 from 'd3';
import {
  Users, Eye, MessageSquare, Clock, Globe, Monitor, Smartphone,
  Search, RefreshCw, Filter, UserCheck, TrendingUp, Activity,
  ChevronRight, ExternalLink, Mail, Phone, MapPin, FileText,
  Zap, Target, MousePointer
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Country flag emoji helper
const getCountryFlag = (code) => {
  if (!code || code === 'UN' || code.length !== 2) return '🌐';
  const codePoints = code
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

// Engagement level colors
const RING_COLORS = {
  inner: { bg: '#10b981', glow: 'rgba(16, 185, 129, 0.4)', label: 'High Intent' },
  middle: { bg: '#f59e0b', glow: 'rgba(245, 158, 11, 0.4)', label: 'Medium' },
  outer: { bg: '#6b7280', glow: 'rgba(107, 114, 128, 0.3)', label: 'Browsing' }
};

const LiveTrafficVisualization = () => {
  const { token, user } = useAuth();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  
  // State
  const [visitors, setVisitors] = useState([]);
  const [stats, setStats] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isOnline, setIsOnline] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 600, height: 600 });
  const [demoMode, setDemoMode] = useState(false);
  
  // Generate mock visitors for demo
  const generateMockVisitors = () => {
    const countries = ['US', 'GB', 'DE', 'FR', 'AE', 'IN', 'JP', 'BR', 'CA', 'AU'];
    const pages = ['/pricing', '/features', '/about', '/contact', '/products', '/demo', '/blog'];
    const browsers = ['Chrome', 'Safari', 'Firefox', 'Edge'];
    const names = ['John D.', 'Sarah M.', 'Ahmed K.', 'Maria G.', 'James W.', 'Lisa P.', 'David L.', 'Emma S.'];
    
    return Array.from({ length: 12 }, (_, i) => {
      const score = Math.random();
      const ring = score >= 0.6 ? 'inner' : score >= 0.3 ? 'middle' : 'outer';
      const timeSeconds = Math.floor(Math.random() * 600);
      
      return {
        id: `demo-${i}`,
        visitor_id: `demo-visitor-${i}`,
        name: names[i % names.length],
        email: i % 3 === 0 ? `user${i}@example.com` : null,
        current_page: pages[Math.floor(Math.random() * pages.length)],
        entry_page: '/',
        referrer: i % 2 === 0 ? 'https://google.com' : null,
        page_views: Math.floor(Math.random() * 10) + 1,
        total_time_seconds: timeSeconds,
        time_on_site: timeSeconds < 60 ? `${timeSeconds}s` : `${Math.floor(timeSeconds / 60)}m ${timeSeconds % 60}s`,
        is_returning: Math.random() > 0.7,
        is_identified: Math.random() > 0.8,
        device_type: Math.random() > 0.7 ? 'mobile' : 'desktop',
        browser: browsers[Math.floor(Math.random() * browsers.length)],
        os: 'Windows',
        country_code: countries[Math.floor(Math.random() * countries.length)],
        city: 'Dubai',
        engagement_score: score,
        ring,
        ring_level: ring === 'inner' ? 1 : ring === 'middle' ? 2 : 3,
        score_breakdown: {
          time: Math.floor(score * 20),
          pages: Math.floor(score * 30),
          returning: score > 0.5 ? 10 : 0,
          identified: score > 0.7 ? 15 : 0,
          recency: Math.floor(score * 25)
        }
      };
    });
  };
  
  // Fetch websites
  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        const res = await axios.get(`${API}/api/live-chat/websites?token=${token}`);
        setWebsites(res.data.websites || []);
      } catch (err) {
        console.error('Error fetching websites:', err);
      }
    };
    fetchWebsites();
  }, [token]);
  
  // Fetch visitors and stats
  const fetchData = useCallback(async () => {
    if (demoMode) {
      // Generate mock data for demo
      const mockVisitors = generateMockVisitors();
      setVisitors(mockVisitors);
      setStats({
        active_visitors: mockVisitors.length,
        visitors_last_hour: 25,
        visitors_today: 156,
        active_chats: 3,
        new_visitors_today: 89,
        returning_visitors_today: 67,
        avg_pages_per_visitor: 4.2,
        avg_time_on_site: 245,
        avg_time_formatted: '4m 5s'
      });
      setLoading(false);
      return;
    }
    
    try {
      const params = new URLSearchParams({ token });
      if (selectedWebsite !== 'all') {
        params.append('website_id', selectedWebsite);
      }
      
      // Progressive — visitors and stats paint as they arrive
      const tasks = [
        axios.get(`${API}/api/live-chat/traffic/active-visitors?${params}`)
          .then((r) => setVisitors(r.data.visitors || []))
          .catch((e) => console.error('[LiveTrafficVisualization] visitors failed:', e)),
        axios.get(`${API}/api/live-chat/traffic/stats?${params}`)
          .then((r) => setStats(r.data.stats))
          .catch((e) => console.error('[LiveTrafficVisualization] stats failed:', e)),
      ];
      let done = false;
      const off = () => { if (!done) { done = true; setLoading(false); } };
      const safety = setTimeout(off, 3000);
      Promise.race(tasks).then(off);
      Promise.allSettled(tasks).finally(() => clearTimeout(safety));
    } catch (err) {
      console.error('Error fetching traffic data:', err);
      toast.error('Failed to load traffic data');
      setLoading(false);
    }
  }, [token, selectedWebsite, demoMode]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Auto-refresh every 5 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);
  
  // Handle container resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ 
          width: Math.max(400, width), 
          height: Math.max(400, Math.min(height, 600)) 
        });
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);
  
  // D3 Radial Visualization
  useEffect(() => {
    if (!svgRef.current || loading) return;
    
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    
    const { width, height } = dimensions;
    const centerX = width / 2;
    const centerY = height / 2;
    const maxRadius = Math.min(width, height) / 2 - 60;
    
    // Ring radii
    const rings = [
      { level: 1, radius: maxRadius * 0.35, color: RING_COLORS.inner },
      { level: 2, radius: maxRadius * 0.6, color: RING_COLORS.middle },
      { level: 3, radius: maxRadius * 0.85, color: RING_COLORS.outer }
    ];
    
    // Create gradient definitions
    const defs = svg.append('defs');
    
    // Radial gradient for center
    const centerGradient = defs.append('radialGradient')
      .attr('id', 'centerGradient')
      .attr('cx', '50%').attr('cy', '50%').attr('r', '50%');
    centerGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#10b981');
    centerGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#059669');
    
    // Main group
    const g = svg.append('g')
      .attr('transform', `translate(${centerX}, ${centerY})`);
    
    // Draw ring guidelines (subtle)
    rings.forEach(ring => {
      g.append('circle')
        .attr('r', ring.radius)
        .attr('fill', 'none')
        .attr('stroke', ring.color.bg)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,4')
        .attr('opacity', 0.3);
    });
    
    // Draw ring labels
    rings.forEach(ring => {
      g.append('text')
        .attr('x', ring.radius + 5)
        .attr('y', -5)
        .attr('fill', ring.color.bg)
        .attr('font-size', '10px')
        .attr('font-weight', '500')
        .attr('opacity', 0.6)
        .text(ring.color.label);
    });
    
    // Center agent node
    const centerGroup = g.append('g').attr('class', 'center-agent');
    
    centerGroup.append('circle')
      .attr('r', 40)
      .attr('fill', 'url(#centerGradient)')
      .attr('filter', 'drop-shadow(0 4px 12px rgba(16, 185, 129, 0.4))');
    
    centerGroup.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', 'white')
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .text('YOU');
    
    // Pulse animation for center
    const pulse = centerGroup.append('circle')
      .attr('r', 40)
      .attr('fill', 'none')
      .attr('stroke', '#10b981')
      .attr('stroke-width', 2);
    
    function animatePulse() {
      pulse
        .attr('r', 40)
        .attr('opacity', 1)
        .transition()
        .duration(2000)
        .ease(d3.easeQuadOut)
        .attr('r', 70)
        .attr('opacity', 0)
        .on('end', animatePulse);
    }
    animatePulse();
    
    // Filter visitors by search
    const filteredVisitors = visitors.filter(v => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        v.name?.toLowerCase().includes(search) ||
        v.email?.toLowerCase().includes(search) ||
        v.current_page?.toLowerCase().includes(search) ||
        v.city?.toLowerCase().includes(search)
      );
    });
    
    // Position visitors in their rings
    const ringGroups = { inner: [], middle: [], outer: [] };
    filteredVisitors.forEach(v => {
      ringGroups[v.ring].push(v);
    });
    
    // Draw visitors
    Object.entries(ringGroups).forEach(([ringName, ringVisitors]) => {
      const ring = rings.find(r => 
        (ringName === 'inner' && r.level === 1) ||
        (ringName === 'middle' && r.level === 2) ||
        (ringName === 'outer' && r.level === 3)
      );
      
      if (!ring || ringVisitors.length === 0) return;
      
      const angleStep = (2 * Math.PI) / Math.max(ringVisitors.length, 1);
      const startAngle = -Math.PI / 2; // Start from top
      
      ringVisitors.forEach((visitor, i) => {
        const angle = startAngle + angleStep * i + (Math.random() * 0.2 - 0.1);
        const jitter = (Math.random() * 20 - 10);
        const x = Math.cos(angle) * (ring.radius + jitter);
        const y = Math.sin(angle) * (ring.radius + jitter);
        
        const visitorGroup = g.append('g')
          .attr('class', 'visitor-node')
          .attr('transform', `translate(${x}, ${y})`)
          .style('cursor', 'pointer')
          .on('click', () => {
            setSelectedVisitor(visitor);
            setSheetOpen(true);
          })
          .on('mouseenter', function() {
            d3.select(this).select('circle')
              .transition()
              .duration(200)
              .attr('r', 28)
              .attr('filter', `drop-shadow(0 4px 16px ${ring.color.glow})`);
          })
          .on('mouseleave', function() {
            d3.select(this).select('circle')
              .transition()
              .duration(200)
              .attr('r', 24)
              .attr('filter', `drop-shadow(0 2px 8px ${ring.color.glow})`);
          });
        
        // Background circle
        visitorGroup.append('circle')
          .attr('r', 24)
          .attr('fill', '#1e293b')
          .attr('stroke', ring.color.bg)
          .attr('stroke-width', 2)
          .attr('filter', `drop-shadow(0 2px 8px ${ring.color.glow})`);
        
        // Flag emoji
        visitorGroup.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', '0.35em')
          .attr('font-size', '16px')
          .text(getCountryFlag(visitor.country_code));
        
        // Name label below
        visitorGroup.append('text')
          .attr('text-anchor', 'middle')
          .attr('y', 36)
          .attr('fill', '#94a3b8')
          .attr('font-size', '9px')
          .text(visitor.name?.substring(0, 12) || 'Visitor');
        
        // Time badge
        visitorGroup.append('rect')
          .attr('x', 12)
          .attr('y', -28)
          .attr('width', 32)
          .attr('height', 14)
          .attr('rx', 7)
          .attr('fill', ring.color.bg);
        
        visitorGroup.append('text')
          .attr('x', 28)
          .attr('y', -18)
          .attr('text-anchor', 'middle')
          .attr('fill', 'white')
          .attr('font-size', '8px')
          .attr('font-weight', '600')
          .text(visitor.time_on_site);
        
        // Entrance animation
        visitorGroup
          .attr('opacity', 0)
          .transition()
          .delay(i * 50)
          .duration(500)
          .attr('opacity', 1);
      });
    });
    
  }, [visitors, loading, dimensions, searchTerm]);
  
  // Start chat handler
  const handleStartChat = async (visitor) => {
    try {
      toast.success(`Starting chat with ${visitor.name}...`);
      // Navigate to chat or trigger chat modal
      // This would integrate with existing chat system
    } catch (err) {
      toast.error('Failed to start chat');
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Top Bar */}
      <div className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-white flex items-center gap-2">
              <Activity className="h-5 w-5 text-emerald-500" />
              Live Traffic
            </h1>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                placeholder="Search visitors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Select value={selectedWebsite} onValueChange={setSelectedWebsite}>
              <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="All Websites" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Websites</SelectItem>
                {websites.map(w => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-400">Auto-refresh</Label>
              <Switch
                checked={autoRefresh}
                onCheckedChange={setAutoRefresh}
              />
            </div>
            
            <div className="flex items-center gap-2 pl-2 border-l border-slate-700">
              <Label className="text-sm text-amber-400">Demo</Label>
              <Switch
                checked={demoMode}
                onCheckedChange={setDemoMode}
              />
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            
            <div className="flex items-center gap-2 ml-4">
              <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-slate-500'}`} />
              <span className="text-sm text-slate-400">{isOnline ? 'Online' : 'Offline'}</span>
              <Switch
                checked={isOnline}
                onCheckedChange={setIsOnline}
              />
            </div>
            
            <div className="flex items-center gap-2 pl-4 border-l border-slate-700">
              <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-sm font-medium">
                {user?.full_name?.charAt(0) || 'A'}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex h-[calc(100vh-57px)]">
        {/* Sidebar Stats */}
        <div className="w-72 border-r border-slate-800 bg-slate-900/30 p-4 space-y-4 overflow-y-auto">
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Live Stats</h3>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Users className="h-5 w-5 text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stats?.active_visitors || 0}</p>
                      <p className="text-xs text-slate-500">Online Now</p>
                    </div>
                  </div>
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats?.active_chats || 0}</p>
                    <p className="text-xs text-slate-500">Active Chats</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Eye className="h-5 w-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats?.visitors_today || 0}</p>
                    <p className="text-xs text-slate-500">Today's Visitors</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats?.avg_time_formatted || '0s'}</p>
                    <p className="text-xs text-slate-500">Avg. Time</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="space-y-3 pt-4 border-t border-slate-800">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Engagement Rings</h3>
            
            {Object.entries(RING_COLORS).map(([key, config]) => {
              const count = visitors.filter(v => v.ring === key).length;
              return (
                <div key={key} className="flex items-center justify-between p-2 rounded-lg bg-slate-800/30">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.bg }} />
                    <span className="text-sm text-slate-300">{config.label}</span>
                  </div>
                  <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                    {count}
                  </Badge>
                </div>
              );
            })}
          </div>
          
          <div className="space-y-3 pt-4 border-t border-slate-800">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Quick Stats</h3>
            
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 rounded-lg bg-slate-800/30 text-center">
                <p className="text-lg font-semibold text-white">{stats?.new_visitors_today || 0}</p>
                <p className="text-xs text-slate-500">New</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/30 text-center">
                <p className="text-lg font-semibold text-white">{stats?.returning_visitors_today || 0}</p>
                <p className="text-xs text-slate-500">Returning</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/30 text-center">
                <p className="text-lg font-semibold text-white">{stats?.avg_pages_per_visitor || 0}</p>
                <p className="text-xs text-slate-500">Avg Pages</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/30 text-center">
                <p className="text-lg font-semibold text-white">{stats?.visitors_last_hour || 0}</p>
                <p className="text-xs text-slate-500">Last Hour</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Visualization Area */}
        <div className="flex-1 flex">
          <div 
            ref={containerRef}
            className="flex-1 flex items-center justify-center p-8"
          >
            {loading ? (
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500" />
                <p className="text-slate-500">Loading visitors...</p>
              </div>
            ) : visitors.length === 0 ? (
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center">
                  <Users className="h-10 w-10 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-slate-300">No active visitors</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Visitors will appear here when they're active on your website
                  </p>
                </div>
              </div>
            ) : (
              <svg
                ref={svgRef}
                width={dimensions.width}
                height={dimensions.height}
                className="overflow-visible"
              />
            )}
          </div>
          
          {/* Right Panel - Prioritized List */}
          <div className="w-80 border-l border-slate-800 bg-slate-900/30 p-4 overflow-y-auto">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Target className="h-4 w-4 text-emerald-500" />
                  Qualified Leads
                </h3>
                <Badge variant="outline" className="text-xs border-emerald-600 text-emerald-500">
                  {visitors.filter(v => v.ring === 'inner').length} high intent
                </Badge>
              </div>
              
              <div className="space-y-2">
                {visitors
                  .sort((a, b) => b.engagement_score - a.engagement_score)
                  .slice(0, 15)
                  .map((visitor) => (
                    <div
                      key={visitor.visitor_id}
                      className="p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 cursor-pointer transition-colors border border-transparent hover:border-slate-700"
                      onClick={() => {
                        setSelectedVisitor(visitor);
                        setSheetOpen(true);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-lg"
                              style={{ 
                                backgroundColor: RING_COLORS[visitor.ring].bg + '20',
                                border: `2px solid ${RING_COLORS[visitor.ring].bg}`
                              }}
                            >
                              {getCountryFlag(visitor.country_code)}
                            </div>
                            <div 
                              className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[8px]"
                              style={{ backgroundColor: RING_COLORS[visitor.ring].bg }}
                            >
                              {Math.round(visitor.engagement_score * 100)}
                            </div>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-white truncate">
                              {visitor.name}
                            </p>
                            <p className="text-xs text-slate-500 truncate">
                              {visitor.current_page?.split('/').pop() || 'Home'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-medium text-slate-300">
                            {visitor.time_on_site}
                          </p>
                          <p className="text-xs text-slate-500">
                            {visitor.page_views} pages
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              
              {visitors.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <p className="text-sm">No visitors to display</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Visitor Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-[400px] bg-slate-900 border-slate-800 text-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                style={{ 
                  backgroundColor: selectedVisitor ? RING_COLORS[selectedVisitor.ring].bg + '20' : '#1e293b',
                  border: selectedVisitor ? `2px solid ${RING_COLORS[selectedVisitor.ring].bg}` : 'none'
                }}
              >
                {selectedVisitor && getCountryFlag(selectedVisitor.country_code)}
              </div>
              <div>
                <p className="text-lg">{selectedVisitor?.name || 'Visitor'}</p>
                <Badge 
                  className="text-xs mt-1"
                  style={{ backgroundColor: selectedVisitor ? RING_COLORS[selectedVisitor.ring].bg : '#6b7280' }}
                >
                  {selectedVisitor ? RING_COLORS[selectedVisitor.ring].label : 'Unknown'}
                </Badge>
              </div>
            </SheetTitle>
          </SheetHeader>
          
          {selectedVisitor && (
            <div className="mt-6 space-y-6">
              {/* Quick Actions */}
              <div className="flex gap-2">
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleStartChat(selectedVisitor)}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Start Chat
                </Button>
                <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                  <UserCheck className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Engagement Score */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-400">Engagement Score</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="text-4xl font-bold text-emerald-500">
                      {Math.round(selectedVisitor.engagement_score * 100)}
                    </div>
                    <div className="flex-1">
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full transition-all duration-500"
                          style={{ 
                            width: `${selectedVisitor.engagement_score * 100}%`,
                            backgroundColor: RING_COLORS[selectedVisitor.ring].bg
                          }}
                        />
                      </div>
                      <div className="flex justify-between mt-1 text-xs text-slate-500">
                        <span>Low</span>
                        <span>High</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Session Info */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-slate-400">Session Details</h4>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-slate-800/50">
                    <div className="flex items-center gap-2 text-slate-400 mb-1">
                      <Clock className="h-3 w-3" />
                      <span className="text-xs">Time on Site</span>
                    </div>
                    <p className="text-lg font-semibold text-white">{selectedVisitor.time_on_site}</p>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-slate-800/50">
                    <div className="flex items-center gap-2 text-slate-400 mb-1">
                      <FileText className="h-3 w-3" />
                      <span className="text-xs">Pages Viewed</span>
                    </div>
                    <p className="text-lg font-semibold text-white">{selectedVisitor.page_views}</p>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-slate-800/50">
                    <div className="flex items-center gap-2 text-slate-400 mb-1">
                      {selectedVisitor.device_type === 'mobile' ? (
                        <Smartphone className="h-3 w-3" />
                      ) : (
                        <Monitor className="h-3 w-3" />
                      )}
                      <span className="text-xs">Device</span>
                    </div>
                    <p className="text-sm font-medium text-white capitalize">{selectedVisitor.device_type}</p>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-slate-800/50">
                    <div className="flex items-center gap-2 text-slate-400 mb-1">
                      <Globe className="h-3 w-3" />
                      <span className="text-xs">Browser</span>
                    </div>
                    <p className="text-sm font-medium text-white">{selectedVisitor.browser}</p>
                  </div>
                </div>
              </div>
              
              {/* Current Page */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-slate-400">Current Page</h4>
                <div className="p-3 rounded-lg bg-slate-800/50 flex items-center gap-2">
                  <MousePointer className="h-4 w-4 text-emerald-500" />
                  <p className="text-sm text-white truncate flex-1">{selectedVisitor.current_page || 'Unknown'}</p>
                  <ExternalLink className="h-4 w-4 text-slate-500" />
                </div>
              </div>
              
              {/* Entry Page */}
              {selectedVisitor.entry_page && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-400">Entry Page</h4>
                  <div className="p-3 rounded-lg bg-slate-800/50">
                    <p className="text-sm text-slate-300 truncate">{selectedVisitor.entry_page}</p>
                  </div>
                </div>
              )}
              
              {/* Referrer */}
              {selectedVisitor.referrer && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-400">Referrer</h4>
                  <div className="p-3 rounded-lg bg-slate-800/50">
                    <p className="text-sm text-slate-300 truncate">{selectedVisitor.referrer}</p>
                  </div>
                </div>
              )}
              
              {/* Contact Info (if identified) */}
              {(selectedVisitor.email || selectedVisitor.is_identified) && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-400">Contact Info</h4>
                  <div className="space-y-2">
                    {selectedVisitor.email && (
                      <div className="p-3 rounded-lg bg-slate-800/50 flex items-center gap-2">
                        <Mail className="h-4 w-4 text-blue-500" />
                        <p className="text-sm text-white">{selectedVisitor.email}</p>
                      </div>
                    )}
                    {selectedVisitor.city && (
                      <div className="p-3 rounded-lg bg-slate-800/50 flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-amber-500" />
                        <p className="text-sm text-white">{selectedVisitor.city}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Score Breakdown */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-slate-400">Score Breakdown</h4>
                <div className="space-y-2">
                  {Object.entries(selectedVisitor.score_breakdown || {}).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 capitalize">{key}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500"
                            style={{ width: `${Math.min(value / 30 * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-300 w-8 text-right">{value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                {selectedVisitor.is_returning && (
                  <Badge variant="outline" className="border-purple-600 text-purple-400">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    Returning
                  </Badge>
                )}
                {selectedVisitor.is_identified && (
                  <Badge variant="outline" className="border-blue-600 text-blue-400">
                    <UserCheck className="h-3 w-3 mr-1" />
                    Identified
                  </Badge>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default LiveTrafficVisualization;
